// /tickets/general — workspace triage grouped by real-time activity.
// Three sections (Now / Ongoing / Upcoming) consuming
// fetchGeneralTriage via the shared ticketStore singleton.
// Filtered by the cross-page assignee tab
// (filterAssignee: null | 'team' | 'agents' | 'me') from TicketsHeader.
//
// Bucket definitions (from fetchGeneralTriage):
//   • now           — ticket.state='in_progress' AND assignee_email matches
//                     an active agent presence (heartbeat < 10m). OR
//                     agent_presence.current_ticket_id = ticket.id (explicit).
//   • ongoing       — state='in_progress' OR has a 'picked_up' activity log entry,
//                     but the assigned agent's heartbeat is older than 10m.
//   • upcoming      — state='not_started' / null, never picked up
//   • pendingApproval — state='awaiting_approval' (surfaced separately with Approve button)
//   • readyToTest   — state='ready_to_test' (right column; QA-pending lane)
//
// Perf (ticket #4498):
//   - Uses shared ticketStore singleton — one fetch + one realtime channel
//     regardless of how many bucket components are mounted.
//   - Initial fetch is limited (INITIAL_LIMIT = 200) for fast first paint;
//     background full-refresh runs 200ms later.
//   - Fetch timeout (10s) + retry CTA on failure (no more silent "Loading…").
//   - AbortController cancels the fetch on page unmount.
//
// Realtime: ticketStore manages the subscription internally.
// Optimistic patches are applied via ticketStore.patch().

import { pageBoilerplateFetch } from '../../functions/tickets/ticketUtils.js'
import { STATE_PROGRESS_PCT } from '../../functions/tickets/fetchGeneralTriage.js'
import { subscribeTicketsRealtime } from '../../functions/tickets/subscribeTicketsRealtime.js'
import { ticketStore } from '../../functions/tickets/ticketStore.js'

const FETCH_TIMEOUT_MS = 10_000  // 10s before showing retry CTA

// Apply a single ticket row update in-place across all triage buckets.
// Moves the ticket to a different bucket only if its state changed.
// Does NOT re-compute parent breadcrumbs or progress — leave that to
// the debounced full refetch. Returns the next triage object (or null
// if nothing changed).
const _applyTicketPatch = (triage, newRow) => {
  if (!triage || !newRow || !newRow.id) return null
  const BUCKETS = ['now', 'ongoing', 'blocked', 'upcoming', 'pendingApproval', 'readyToTest', 'decisions']
  let found = null
  let foundBucket = null
  for (const bk of BUCKETS) {
    const list = triage[bk] || []
    const idx = list.findIndex(t => t.id === newRow.id)
    if (idx !== -1) { found = list[idx]; foundBucket = bk; break }
  }
  if (!found) return null  // ticket not currently in triage (e.g. different type)

  const updated = {
    ...found,
    state: newRow.state ?? found.state,
    assignee_email: newRow.assignee_email ?? found.assignee_email,
    priority: newRow.priority ?? found.priority,
    labels: newRow.labels ?? found.labels,
    title: newRow.title ?? found.title,
    updatedAt: newRow.updated_at ?? found.updatedAt
  }

  const next = { ...triage }
  for (const bk of BUCKETS) next[bk] = [...(triage[bk] || [])]
  next[foundBucket] = next[foundBucket].filter(t => t.id !== newRow.id)
  const st = updated.state
  if (st === 'done' || st === 'closed') {
    // Done tickets leave triage entirely.
  } else if (updated.type === 'decision') {
    // Decision tickets always stay in the decisions bucket regardless of state.
    next.decisions = [updated, ...next.decisions]
  } else if (st === 'ready_to_test') {
    next.readyToTest = [updated, ...next.readyToTest]
  } else if (st === 'awaiting_approval') {
    next.pendingApproval = [updated, ...next.pendingApproval]
  } else if (st === 'in_progress') {
    next.now = [{ ...updated, progressPct: STATE_PROGRESS_PCT.in_progress || 33 }, ...next.now]
  } else if (st === 'blocked') {
    next.blocked = [{ ...updated, isBlocked: true }, ...next.blocked]
  } else {
    next.upcoming = [updated, ...next.upcoming]
  }
  return next
}

// Apply an agent_presence row update in-place.
const _applyPresencePatch = (triage, newRow) => {
  if (!triage || !newRow) return null
  const presence = triage.agentPresence || []
  const idx = presence.findIndex(p => p.agent_name === newRow.agent_name ||
    p.assignee_email === newRow.assignee_email)
  let nextPresence
  if (idx !== -1) {
    nextPresence = presence.map((p, i) => i === idx ? { ...p, ...newRow } : p)
  } else {
    nextPresence = [...presence, newRow]
  }
  return { ...triage, agentPresence: nextPresence }
}

export const general = {
  extends: 'AppPage',
  metadata: { title: 'General / Tickets / Symbols Workspace' },

  fetch: pageBoilerplateFetch,

  onCreate: async (el, s) => {
    // Mark loading start.
    s.root.update({ triageLoading: true, triageError: null })

    // Read URL params.
    // Always clear filterEpic first so stale SPA state from a prior navigation
    // does not silently filter out tickets on the new load.
    const urlPatch = { filterEpic: null }
    if (typeof window !== 'undefined') {
      const url = new URL(window.location)
      const epic = url.searchParams.get('epic')
      if (epic) urlPatch.filterEpic = epic
      const groupBy = url.searchParams.get('groupBy')
      if (groupBy) urlPatch.generalGroupBy = groupBy
    }
    s.root.update(urlPatch)

    // If the store already has data (SPA page-switch back), use it immediately.
    const cached = ticketStore.current()
    if (cached) {
      s.root.update({ triage: cached, triageLoading: false })
    }

    // Subscribe to store updates (handles future store refreshes).
    // Also serves as the first-data callback when no cached data is available.
    let _firstDataReceived = !!cached
    el._storeUnsub = ticketStore.subscribe((triage) => {
      _firstDataReceived = true
      s.root.update({ triage, triageLoading: false, triageError: null })
    })

    // Timeout guard — if no data arrives within FETCH_TIMEOUT_MS, show retry CTA.
    if (!cached) {
      const timeoutId = setTimeout(() => {
        if (!_firstDataReceived) {
          s.root.update({ triageLoading: false, triageError: 'timeout' })
        }
      }, FETCH_TIMEOUT_MS)
      // Store cleanup so we don't fire after page unmount.
      el._triageTimeoutId = timeoutId
    }

    // Set up page-level realtime (optimistic patches + debounced refresh).
    // The ticketStore also has its own subscription for background refreshes;
    // the page-level one handles optimistic patches specific to this view.
    el._rtUnsub = subscribeTicketsRealtime({
      pageName: 'general',
      tables: ['tickets', 'agent_presence', 'agent_activity_log'],

      onOptimisticPatch: (table, eventType, row) => {
        const current = s.root.triage
        if (!current) return
        let next = null
        if (table === 'tickets' && eventType === 'UPDATE') {
          next = _applyTicketPatch(current, row)
        } else if (table === 'agent_presence') {
          next = _applyPresencePatch(current, row)
        }
        if (next) {
          ticketStore.patch(next)  // update store so all subscribers see it
        }
      },

      onRefresh: async () => {
        await ticketStore.refresh()  // store notifies all subscribers
      }
    })
  },

  onRemove: (el) => {
    if (el._triageTimeoutId) { clearTimeout(el._triageTimeoutId); el._triageTimeoutId = null }
    if (el._storeUnsub) { el._storeUnsub(); el._storeUnsub = null }
    if (el._rtUnsub) { el._rtUnsub(); el._rtUnsub = null }
    const root = el.getRoot()
    if (root && root.selectedTicket) root.update({ selectedTicket: null })
  },

  TicketsSidebar: {},
  TicketsGeneralMain: {},
  TicketsNewTicketModal: {},
  TicketsDetailModal: {}
}
